import { useNavigate } from "react-router-dom";

import { useChannels } from "../hooks/useChannels";

import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { useChannelInvoiceTableColumns } from "@/lib/tableColumns/useChannelInvoiceTable";
import { getCurrentYearMonth } from "@/lib/utils/dateUtils";
import { ChannelType } from "@/types/response/ChannelType";

const InvoiceChannelsPage = () => {
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { tenantId } = useAuth();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const navigate = useNavigate();
  const handleSort = useTableSortHandler<ChannelType>();
  // Get current year and month using the utility function
  const [year, month] = getCurrentYearMonth();
  const previousMonth =
    month === "01" ? "12" : String(Number(month) - 1).padStart(2, "0");
  const previousYear = month === "01" ? String(Number(year) - 1) : year;

  const {
    data: channels,
    isLoading: channelsLoading,
    isFetching: channelsFetching,
  } = useChannels({
    tenantId,
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
    isInvoiced: false,
  });

  const { channelTableColumns } = useChannelInvoiceTableColumns();

  const handleRowClick = (row: ChannelType) => {
    navigate(
      `/${ROUTES.invoices}/${ROUTES.channels}/${row.id}/${ROUTES.toDo}/${previousYear}/${previousMonth}`,
      { state: { channelName: row.name } },
    );
  };

  return (
    <div className="flex flex-col gap-8">
      <TableToolbar onSearchChange={setSearchValue} searchValue={searchValue} />

      <DataTable
        columns={channelTableColumns}
        data={channels?.data}
        isLoading={channelsLoading || channelsFetching}
        onRowClick={handleRowClick}
        orderBy={orderBy}
        descending={descending}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        totalRecords={channels?.totalRecords ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(row) => row.id}
      />
    </div>
  );
};

export default InvoiceChannelsPage;
